import React from "react";
import { Plus, Trash2, TriangleAlert } from "lucide-react";
import { Button } from "@pgs-core/ui/components/button";
import { Input } from "@pgs-core/ui/components/input";
import {
  WEEK_DISPLAY_ORDER,
  WEEKDAY_LABELS,
  isWithinOpeningHours,
  type HoursRule,
  type WeeklyHours,
} from "../lib/opening-hours";

/**
 * The weekly hours grid: windows per day with add/remove, Monday-first.
 * Shared by Settings → Opening Hours and the booking module's per-service
 * hours. Controlled — the parent owns the rules array and saving.
 *
 * When `openingHours` is given, windows not fully inside that day's opening
 * hours get a warning flag (allowed, just surfaced).
 */
export function WeeklyHoursGrid({ rules, onChange, openingHours, closedLabel = "Closed" }: {
  rules: HoursRule[];
  onChange: (rules: HoursRule[]) => void;
  openingHours?: WeeklyHours | null;
  closedLabel?: string;
}) {
  const update = (index: number, patch: Partial<HoursRule>) => {
    onChange(rules.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)));
  };

  return (
    <div className="space-y-2">
      {WEEK_DISPLAY_ORDER.map((day) => {
        const dayRules = rules
          .map((rule, index) => ({ rule, index }))
          .filter(({ rule }) => rule.day_of_week === day);
        return (
          <div key={day} className="flex flex-wrap items-start gap-3 border-b py-2 last:border-0">
            <div className="w-28 pt-1.5 text-sm font-medium">{WEEKDAY_LABELS[day]}</div>
            <div className="flex flex-1 flex-col gap-2">
              {dayRules.length === 0 && (
                <span className="pt-1.5 text-sm text-muted-foreground">{closedLabel}</span>
              )}
              {dayRules.map(({ rule, index }) => {
                const flagged = openingHours && rule.start_time && rule.end_time &&
                  !isWithinOpeningHours(rule, openingHours);
                return (
                  <div key={index}>
                    <div className="flex items-center gap-2">
                      <Input
                        type="time" value={rule.start_time} className="w-32"
                        aria-label={`${WEEKDAY_LABELS[day]} from`}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => update(index, { start_time: e.target.value })}
                      />
                      <span className="text-muted-foreground">to</span>
                      <Input
                        type="time" value={rule.end_time} className="w-32"
                        aria-label={`${WEEKDAY_LABELS[day]} until`}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => update(index, { end_time: e.target.value })}
                      />
                      <Button
                        variant="ghost" size="sm" className="text-muted-foreground hover:text-destructive"
                        title="Remove window"
                        onClick={() => onChange(rules.filter((_, i) => i !== index))}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    {flagged && (
                      <p className="mt-1 flex items-center gap-1.5 text-xs text-warning">
                        <TriangleAlert className="h-3.5 w-3.5" />
                        Outside your opening hours — bookable anyway.
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
            <Button
              variant="outline" size="sm"
              onClick={() => onChange([...rules, { day_of_week: day, start_time: "09:00", end_time: "17:00" }])}
            >
              <Plus className="h-3.5 w-3.5" />
              Add hours
            </Button>
          </div>
        );
      })}
    </div>
  );
}
